import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';
import type { PromptBlockEditorRow } from '../services/promptBlocksApi';
import { TUTOR_TEMPLATE_SLOT_KEYS } from '../modules/tutor/promptTemplatePlaceholders';

const LANGUAGE_ID = 'prompt-jinja';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /**
   * When this changes (e.g. template id or create modal open), editor content resets from `value`.
   * Required so we do not pass parent `value` into Monaco on every keystroke — that re-applies the
   * model and clears IntelliSense ("No suggestions.") while typing in create/edit modals.
   */
  syncKey?: string;
  height?: string;
  readOnly?: boolean;
  theme?: 'vs-dark' | 'light';
};

const HOVER_PREVIEW_MAX = 480;

/**
 * Text before cursor. Matches either inside a `{{ ...` segment or a standalone word.
 */
function matchSuggestPrefix(beforeCursor: string): { full: string; keyPrefix: string; isBareWord: boolean } | null {
  /* CRLF lines end with `\r` → `$` fails to match; suggest provider then returns []. */
  const normalized = beforeCursor.replace(/\r/g, '');
  
  // 1) Match inside {{ ... (allows trailing spaces before }})
  const m1 = normalized.match(/\{\{\s*([\w.]*)\s*$/);
  if (m1) {
    return { full: m1[0], keyPrefix: (m1[1] ?? '').toLowerCase(), isBareWord: false };
  }
  
  // 2) Match bare word at cursor (e.g. "ba", "mode_r")
  const m2 = normalized.match(/(?:^|[^a-zA-Z0-9_])([a-zA-Z_][a-zA-Z0-9_]*)$/);
  if (m2) {
    return { full: m2[1], keyPrefix: m2[1].toLowerCase(), isBareWord: true };
  }
  
  return null;
}

/** Default VS Code/Mongo word separators include `{` `}` — breaks suggest filtering for `{{ dia`. */
const PROMPT_EDITOR_WORD_SEPARATORS = '`~!@#$%^&*()-=+[]\\|;:\'",.<>/?';

/** Runtime `str.format` / template slot names only (not Mongo `prompt_blocks.key`). */
const SLOT_ROWS: PromptBlockEditorRow[] = TUTOR_TEMPLATE_SLOT_KEYS.map((s) => ({
  key: s.key,
  content: '',
  description: s.description,
  isActive: true,
}));

function slotSuggestRows(): PromptBlockEditorRow[] {
  return [...SLOT_ROWS].sort((a, b) => a.key.localeCompare(b.key));
}

let languageRegistered = false;

function ensureLanguageAndTheme(monaco: typeof Monaco) {
  if (languageRegistered) return;
  languageRegistered = true;

  monaco.languages.register({ id: LANGUAGE_ID });

  monaco.languages.setLanguageConfiguration(LANGUAGE_ID, {
    wordPattern: /[a-zA-Z0-9_.]+/,
    brackets: [
      ['{', '}'],
      ['{{', '}}'],
    ],
  });

  /* One style for all `{{ ... }}` placeholders */
  monaco.languages.setMonarchTokensProvider(LANGUAGE_ID, {
    tokenizer: {
      root: [[/\{\{[^}]+\}\}/, 'variable.template'], [/[^\{]+/, 'source'], [/[{}]/, 'delimiter']],
    },
  });

  monaco.editor.defineTheme('prompt-editor-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [{ token: 'variable.template', foreground: '79c0ff' }, { token: 'delimiter', foreground: 'd4d4d4' }],
    colors: {},
  });
}

function rowMatchesPrefix(row: PromptBlockEditorRow, prefix: string): boolean {
  if (!prefix) return true;
  const k = row.key.toLowerCase();
  const t = (row.blockType ?? '').toLowerCase();
  const d = (row.description ?? '').toLowerCase();
  return k.includes(prefix) || t.includes(prefix) || d.includes(prefix);
}

function suggestDetail(row: PromptBlockEditorRow): string | undefined {
  const parts = [row.blockType, row.description].filter((p): p is string => Boolean(p && p.trim()));
  if (parts.length === 0) return undefined;
  return parts.join(' · ');
}

export const PromptEditor: React.FC<Props> = ({
  value,
  onChange,
  syncKey,
  height = 'min(440px, 60vh)',
  readOnly,
  theme = 'vs-dark',
}) => {
  const [doc, setDoc] = useState(value);
  const prevSyncKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const sk = syncKey ?? '__default__';
    if (prevSyncKeyRef.current !== sk) {
      prevSyncKeyRef.current = sk;
      setDoc(value);
    }
  }, [syncKey, value]);

  const handleDocChange = useCallback(
    (v: string | undefined) => {
      const next = v ?? '';
      setDoc(next);
      onChange(next);
    },
    [onChange]
  );

  const modelPath = `inmemory:/tkm/prompt-editor/${(syncKey ?? 'draft').replace(/[^\w.-]/g, '_')}`;

  const suggestRows = useMemo(() => slotSuggestRows(), []);

  const itemsRef = useRef<PromptBlockEditorRow[]>([]);
  const hoverMapRef = useRef<Map<string, PromptBlockEditorRow>>(new Map());
  const slotDescRef = useRef<Map<string, string>>(new Map(TUTOR_TEMPLATE_SLOT_KEYS.map((s) => [s.key, s.description])));

  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);

  const completionDispRef = useRef<Monaco.IDisposable | null>(null);
  const hoverDispRef = useRef<Monaco.IDisposable | null>(null);
  const suggestTriggerDispRef = useRef<Monaco.IDisposable | null>(null);

  itemsRef.current = suggestRows;
  useEffect(() => {
    hoverMapRef.current = new Map(suggestRows.map((b) => [b.key, b]));
  }, [suggestRows]);

  const registerProviders = useCallback((editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) => {
    completionDispRef.current?.dispose();
    hoverDispRef.current?.dispose();
    suggestTriggerDispRef.current?.dispose();

    completionDispRef.current = monaco.languages.registerCompletionItemProvider(LANGUAGE_ID, {
      triggerCharacters: ['{', ' ', '_'],
      provideCompletionItems(model: Monaco.editor.ITextModel, position: Monaco.Position) {
        const line = model.getLineContent(position.lineNumber);
        const before = line.slice(0, position.column - 1).replace(/\r/g, '');
        const match = matchSuggestPrefix(before);
        if (!match) {
          return { suggestions: [] };
        }
        const { full: replaceRun, keyPrefix: prefix, isBareWord } = match;
        const replaceLen = replaceRun.length;
        const startColumn = position.column - replaceLen;

        const range = new monaco.Range(position.lineNumber, startColumn, position.lineNumber, position.column);

        const latest = itemsRef.current.length > 0 ? itemsRef.current : SLOT_ROWS;

        const after = line.slice(position.column - 1);
        const hasClosingBrace = after.trimStart().startsWith('}}');
        
        const insert = (k: string) => {
          if (isBareWord) {
            return `{{ ${k} }}`;
          } else {
            return hasClosingBrace ? `{{ ${k} ` : `{{ ${k} }}`;
          }
        };

        const matches = latest.filter((b) => rowMatchesPrefix(b, prefix));
        const suggestions: Monaco.languages.CompletionItem[] = matches.map((b, i) => {
          const text = insert(b.key);
          const detail = suggestDetail(b);
          const docMd = [b.blockType && `**Type:** ${b.blockType}`, b.description && b.description].filter(Boolean).join('\n\n');
          
          // Ensure Monaco doesn't filter out the suggestion because of the `{{` prefix.
          // By adding the typed prefix punctuation to filterText, the editor text fully matches.
          const punctuationPrefix = replaceRun.slice(0, replaceRun.length - prefix.length);
          const baseFilter = [b.key, b.blockType ?? '', b.description ?? ''].join(' ').trim();
          const filterStr = punctuationPrefix + baseFilter;
          
          return {
            label: b.key,
            kind: monaco.languages.CompletionItemKind.Variable,
            detail,
            documentation: docMd ? { value: docMd } : undefined,
            insertText: text,
            range,
            filterText: filterStr,
            sortText: b.key,
            preselect: i === 0,
          };
        });

        return { suggestions };
      },
    });

    hoverDispRef.current = monaco.languages.registerHoverProvider(LANGUAGE_ID, {
      provideHover(model: Monaco.editor.ITextModel, position: Monaco.Position) {
        const line = model.getLineContent(position.lineNumber);
        const col = position.column;
        const lineRe = /\{\{([^}]+)\}\}/g;
        let hm: RegExpExecArray | null;
        while ((hm = lineRe.exec(line))) {
          const startCol = hm.index + 1;
          const endColExclusive = hm.index + hm[0].length + 1;
          if (col >= startCol && col < endColExclusive) {
            const key = hm[1].trim();
            const block = hoverMapRef.current.get(key);
            const slotDesc = slotDescRef.current.get(key);
            const body = block?.content ?? '';
            const preview = body.length > HOVER_PREVIEW_MAX ? `${body.slice(0, HOVER_PREVIEW_MAX)}…` : body;
            const title =
              block?.description ||
              slotDesc ||
              (body ? 'Prompt block content' : 'Template variable');
            const typeBit = block?.blockType ? `${block.blockType} · ` : '';
            const header = `**${key}** — ${typeBit}${title}`;
            const codeBlock = preview || (slotDesc && !body ? slotDesc : '(empty)');
            return {
              range: new monaco.Range(position.lineNumber, startCol, position.lineNumber, endColExclusive),
              contents: [
                { value: header, isTrusted: true },
                { value: `\n\n\`\`\`\n${codeBlock}\n\`\`\``, isTrusted: true },
              ],
            };
          }
        }
        return null;
      },
    });

    /* Help Monaco open the suggest widget after `{{` (custom languages often need a nudge) */
    suggestTriggerDispRef.current = editor.onDidChangeModelContent(() => {
      const model = editor.getModel();
      if (!model) return;
      const pos = editor.getPosition();
      if (!pos) return;
      const line = model.getLineContent(pos.lineNumber);
      const before = line.slice(0, pos.column - 1).replace(/\r/g, '');
      if (before.match(/\{\{\s*[\w.]*$/)) {
        window.setTimeout(() => {
          editor.trigger('prompt-editor', 'editor.action.triggerSuggest', {});
        }, 0);
      }
    });
  }, []);

  useEffect(
    () => () => {
      completionDispRef.current?.dispose();
      hoverDispRef.current?.dispose();
      suggestTriggerDispRef.current?.dispose();
    },
    []
  );

  const onMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      monacoRef.current = monaco;
      ensureLanguageAndTheme(monaco);
      monaco.editor.setTheme(theme === 'vs-dark' ? 'prompt-editor-dark' : 'vs');
      registerProviders(editor, monaco);
    },
    [registerProviders, theme]
  );

  return (
    <div className="prompt-editor-wrap rounded-lg border border-app-border dark:border-gray-600 overflow-visible min-h-0">
      <Editor
        path={modelPath}
        height={height}
        language={LANGUAGE_ID}
        theme={theme === 'vs-dark' ? 'prompt-editor-dark' : 'vs'}
        value={doc}
        onChange={handleDocChange}
        onMount={onMount}
        options={{
          /* Suggest dropdown in modals: render as fixed overlay (parent overflow-hidden would break list). */
          fixedOverflowWidgets: true,
          minimap: { enabled: false },
          wordWrap: 'on',
          fontSize: 13,
          scrollBeyondLastLine: false,
          readOnly,
          automaticLayout: true,
          tabSize: 2,
          /* `{` `}` default separators split `{{ dia` so suggest filter sees the wrong “word”. */
          wordSeparators: PROMPT_EDITOR_WORD_SEPARATORS,
          quickSuggestions: { other: true, comments: true, strings: true },
          suggestOnTriggerCharacters: true,
          wordBasedSuggestions: 'off',
          tabCompletion: 'on',
          quickSuggestionsDelay: 0,
          suggest: {
            filterGraceful: true,
            snippetsPreventQuickSuggestions: false,
            matchOnWordStartOnly: false,
          },
        }}
      />
    </div>
  );
};
